export interface MediaInputDedupeKeys {
    identityValues: readonly string[]
    fileKey: string
    urlKey: string
}

/**
 * Keeps the first media input for each identity while indexing every input in one pass.
 *
 * Every input contributes its keys, including an input that was itself a duplicate. This matches
 * Array.findIndex semantics when overlapping identifiers form a chain across multiple inputs.
 */
export function dedupeMediaInputsByKeys<T>(
    inputs: T[],
    keysFor: (input: T) => MediaInputDedupeKeys,
): T[] {
    const seenInputs = new Set<T>()
    const seenIdentityValues = new Set<string>()
    const seenFileKeys = new Set<string>()
    const seenUrlKeys = new Set<string>()

    return inputs.filter(input => {
        const { identityValues, fileKey, urlKey } = keysFor(input)
        const isDuplicate =
            seenInputs.has(input) ||
            identityValues.some(value => seenIdentityValues.has(value)) ||
            (!!fileKey && seenFileKeys.has(fileKey)) ||
            (!!urlKey && seenUrlKeys.has(urlKey))

        seenInputs.add(input)
        identityValues.forEach(value => seenIdentityValues.add(value))
        if (fileKey) seenFileKeys.add(fileKey)
        if (urlKey) seenUrlKeys.add(urlKey)

        return !isDuplicate
    })
}
